import type {
  Mail, MailPage, InvitationResponse, MailThread, MeetingNotes, MeetingPlan, MeetingsPage, MailVerdict,
  ChangeReply,
  ServerCredential,
  SprintsResponse, SprintPlan, PlanLine, LinkedTicket, JiraAnalysis, AnalysisChoice, JiraDeepAnalysis, SprintView,
  ServerLogin,
  InboxItem,
  HiddenInboxItem,
  NotificationStats,
  CostStatus,
  CalendarProposal, DocumentProposal,
  AidevIssue, ImprovementProposal,
  OutboxMessage,
  MessageConversation,
  GoogleStatus, SlackStatus, SlackThread,
  NotifySettings,
  SessionReadState,
  SessionWait,
  Person,
  PersonHistoryEntry,
  PeopleBackfill,
  TeamView,
  DeploymentDetail,
  DeploymentSummary,
  ReviewSummary,
  Ticket,
  TicketConsistency,
  ImplementResult,
  ReplyResult,
  ReviewDetail,
  HeldEvent,
  AggregatedCostsResponse,
  ConsumptionResponse,
  CostsResponse,
  LogEntry,
  Schedule,
  ScheduleRunsResponse,
  SSEEvent,
  Server,
  ServerMetrics,
  SecretsScrub,
  Session,
  WorkspaceTreeResponse,
  WorkspaceFileResponse,
  WorkspaceGitStatusResponse,
  WorkspaceGitBranchesResponse,
  TrustedActor,
  ReassignSuggestion,
  PersonIdentity,
  ActionProposal, SlackProposal, IndexStatus } from './types.js';

export type GetToken = () => Promise<string>;

const API_URL = import.meta.env.VITE_API_URL || '';

export async function request<T>(
  getToken: GetToken,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await getToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(body.message || `Request failed: ${response.status}`);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

export function createApiClient(getToken: GetToken) {
  return {
    servers: {
      list: () => request<Server[]>(getToken, '/servers'),
      get: (serverId: string) => request<Server>(getToken, `/servers/${serverId}`),
      start: (serverId: string) =>
        request<void>(getToken, `/servers/${serverId}/start`, { method: 'POST' }),
      stop: (serverId: string, reason?: string) =>
        request<void>(getToken, `/servers/${serverId}/stop`, {
          method: 'POST',
          body: reason ? JSON.stringify({ reason }) : undefined,
        }),
      metrics: (serverId: string) =>
        request<ServerMetrics>(getToken, `/servers/${serverId}/metrics`),
      logs: (serverId: string, limit = 20) =>
        request<LogEntry[]>(getToken, `/servers/${serverId}/logs?limit=${limit}`),
      scrubSecrets: (serverId: string, body: { apply: boolean; after?: string; part?: 'transcripts' }) =>
        request<SecretsScrub>(getToken, `/servers/${serverId}/secrets/scrub`, { method: 'POST', body: JSON.stringify(body) }),
      reloadSecrets: (serverId: string) =>
        request<{ status: string; secrets: { total: number } }>(getToken, `/servers/${serverId}/secrets/reload`, { method: 'POST' }),
      schedules: (serverId: string) =>
        request<Schedule[]>(getToken, `/servers/${serverId}/schedules`),
      scheduleRuns: (serverId: string, limit = 10) =>
        request<ScheduleRunsResponse>(getToken, `/servers/${serverId}/schedules/runs?limit=${limit}`),
      syncSchedules: (serverId: string) =>
        request<{ created: string[]; updated: string[]; deleted: string[]; unchanged: string[] }>(getToken, `/servers/${serverId}/schedules/sync`, { method: 'POST', body: '{}' }),
    },
    reviews: {
      list: (serverId: string) => request<ReviewSummary[]>(getToken, `/servers/${serverId}/reviews`),
      get: (serverId: string, externalRef: string) =>
        request<ReviewDetail>(getToken, `/servers/${serverId}/reviews/${encodeURIComponent(externalRef)}`),
      start: (serverId: string, pr: string) =>
        request<{ externalRef: string }>(getToken, `/servers/${serverId}/reviews`, { method: 'POST', body: JSON.stringify({ pr }) }),
      postConversationReply: (serverId: string, externalRef: string, body: string) =>
        request<ReviewDetail>(getToken, `/servers/${serverId}/reviews/${encodeURIComponent(externalRef)}/conversation-reply`, { method: 'POST', body: JSON.stringify({ body }) }),
    },
    meetings: {
      list: (serverId: string) => request<MeetingsPage>(getToken, `/servers/${serverId}/meetings`),
      applyPlan: (serverId: string, eventId: string, keys: string[]) =>
        request<{ plan: MeetingPlan }>(getToken, `/servers/${serverId}/meetings/${encodeURIComponent(eventId)}/plan/apply`, { method: 'POST', body: JSON.stringify({ keys }) }),
      dismissPlan: (serverId: string, eventId: string) =>
        request<{ plan: MeetingPlan }>(getToken, `/servers/${serverId}/meetings/${encodeURIComponent(eventId)}/plan/dismiss`, { method: 'POST' }),
      startSession: (serverId: string, eventId: string) =>
        request<{ queued: true; sessionId?: string }>(getToken, `/servers/${serverId}/meetings/${encodeURIComponent(eventId)}/session`, { method: 'POST' }),
      notes: (serverId: string, eventId: string, docId: string) =>
        request<{ notes: MeetingNotes }>(getToken, `/servers/${serverId}/meetings/${encodeURIComponent(eventId)}/notes/${encodeURIComponent(docId)}`),
    },
    mail: {
      list: (serverId: string) => request<MailPage>(getToken, `/servers/${serverId}/mail`),
      verdict: (serverId: string, key: string, verdict: MailVerdict) =>
        request<{ mail: Mail }>(getToken, `/servers/${serverId}/mail/${encodeURIComponent(key)}/verdict`, { method: 'POST', body: JSON.stringify({ verdict }) }),
      createDraft: (serverId: string, threadId: string, body: string) =>
        request<{ thread: MailThread }>(getToken, `/servers/${serverId}/mail/threads/${encodeURIComponent(threadId)}/draft`, { method: 'POST', body: JSON.stringify({ body }) }),
      removeNoise: (serverId: string, sender: string) =>
        request<{ learnedNoise: string[] }>(getToken, `/servers/${serverId}/mail/noise/remove`, { method: 'POST', body: JSON.stringify({ sender }) }),
      askSession: (serverId: string, key: string) =>
        request<{ thread: MailThread }>(getToken, `/servers/${serverId}/mail/${encodeURIComponent(key)}/session`, { method: 'POST' }),
      respond: (serverId: string, threadId: string, response: InvitationResponse) =>
        request<{ thread: MailThread }>(getToken, `/servers/${serverId}/mail/threads/${encodeURIComponent(threadId)}/respond`, { method: 'POST', body: JSON.stringify({ response }) }),
    },
    tickets: {
      list: (serverId: string) => request<{ tickets: Ticket[]; consistency?: TicketConsistency | null }>(getToken, `/servers/${serverId}/tickets`),
      /** A GitLab issue's plan prepared on demand (ADR-041 step 3a) */
      start: (serverId: string, issue: string) => request<{ externalRef: string }>(getToken, `/servers/${serverId}/tickets`, { method: 'POST', body: JSON.stringify({ issue }) }),
      createSubTickets: (serverId: string, ref: string, subTickets: Array<{ title: string; goal: string; dependsOn: number[] }>) =>
        request<{ ticket: Ticket; complete: boolean }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/sub-tickets`, { method: 'POST', body: JSON.stringify({ subTickets }) }),
      checkConsistency: (serverId: string) =>
        request<{ consistency: TicketConsistency }>(getToken, `/servers/${serverId}/tickets/consistency`, { method: 'POST', body: '{}' }),
      assignees: (serverId: string, ref: string) =>
        request<{ assignees: string[]; suggestions?: ReassignSuggestion[] }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/assignees`),
      implement: (serverId: string, ref: string, approach: string, trustAuthors?: string[]) =>
        request<ImplementResult>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/implement`, { method: 'POST', body: JSON.stringify({ approach, ...(trustAuthors ? { trustAuthors } : {}) }) }),
      reassign: (serverId: string, ref: string, login: string, comment: string) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/reassign`, { method: 'POST', body: JSON.stringify({ login, comment }) }),
      reply: (serverId: string, ref: string, comment: string, updatePlan = false) =>
        request<ReplyResult>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/reply`, { method: 'POST', body: JSON.stringify({ comment, ...(updatePlan ? { updatePlan } : {}) }) }),
      dismiss: (serverId: string, ref: string) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/dismiss`, { method: 'POST', body: '{}' }),
      openPullRequest: (serverId: string, ref: string, pr: { commit: string; title: string; body: string; draft: boolean; replies?: ChangeReply[] }) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/open-pr`, { method: 'POST', body: JSON.stringify(pr) }),
      postReplies: (serverId: string, ref: string, replies: ChangeReply[]) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/replies`, { method: 'POST', body: JSON.stringify({ replies }) }),
      nextStep: (serverId: string, ref: string, approach: string) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/next-step`, { method: 'POST', body: JSON.stringify({ approach }) }),
      requestChanges: (serverId: string, ref: string, note: string) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/request-changes`, { method: 'POST', body: JSON.stringify({ note }) }),
      dropChange: (serverId: string, ref: string) =>
        request<{ ticket: Ticket }>(getToken, `/servers/${serverId}/tickets/${encodeURIComponent(ref)}/drop`, { method: 'POST', body: '{}' }),
    },
    actors: {
      list: (serverId: string) => request<{ actors: TrustedActor[] }>(getToken, `/servers/${serverId}/actors`),
      revoke: (serverId: string, ref: string) =>
        request<void>(getToken, `/servers/${serverId}/actors/${encodeURIComponent(ref)}`, { method: 'DELETE' }),
    },
    sprint: (serverId: string) => request<SprintsResponse>(getToken, `/servers/${serverId}/sprint`),
    sprintPlan: (serverId: string, boardId: number) => request<{ plan: SprintPlan | null }>(getToken, `/servers/${serverId}/sprint/${boardId}/plan`),
    preparePlan: (serverId: string, boardId: number, away: string[]) =>
      request<SprintPlan>(getToken, `/servers/${serverId}/sprint/${boardId}/prepare`, { method: 'POST', body: JSON.stringify({ away }) }),
    applyPlan: (serverId: string, boardId: number, lines: PlanLine[]) =>
      request<SprintPlan>(getToken, `/servers/${serverId}/sprint/${boardId}/plan/apply`, { method: 'POST', body: JSON.stringify({ lines }) }),
    dismissPlan: (serverId: string, boardId: number) =>
      request<void>(getToken, `/servers/${serverId}/sprint/${boardId}/plan/dismiss`, { method: 'POST' }),
    sprintAnalyses: (serverId: string) => request<{ analyses: JiraAnalysis[] }>(getToken, `/servers/${serverId}/sprint/analyses`),
    applyAnalysis: (serverId: string, key: string, choice: AnalysisChoice) =>
      request<JiraAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/apply`, { method: 'POST', body: JSON.stringify(choice) }),
    createLinked: (serverId: string, key: string, ticket: LinkedTicket) =>
      request<JiraAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/linked`, { method: 'POST', body: JSON.stringify(ticket) }),
    askAuthor: (serverId: string, key: string, comment: string) =>
      request<JiraAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/ask-author`, { method: 'POST', body: JSON.stringify({ comment }) }),
    analyseTicket: (serverId: string, key: string, boardId: number) =>
      request<JiraAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/analyse`, { method: 'POST', body: JSON.stringify({ boardId }) }),
    discussAnalysis: (serverId: string, key: string) =>
      request<NonNullable<JiraAnalysis['discuss']>>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/discuss`, { method: 'POST' }),
    implementJira: (serverId: string, key: string, body: { repository: string; approach: string }) =>
      request<Ticket>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/implement`, { method: 'POST', body: JSON.stringify(body) }),
    deepAnalysis: (serverId: string, key: string) =>
      request<JiraDeepAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/deep`, { method: 'POST' }),
    postPlan: (serverId: string, key: string, comment: string) =>
      request<JiraDeepAnalysis>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/post-plan`, { method: 'POST', body: JSON.stringify({ comment }) }),
    dismissAnalysis: (serverId: string, key: string) =>
      request<void>(getToken, `/servers/${serverId}/sprint/analyses/${encodeURIComponent(key)}/dismiss`, { method: 'POST' }),
    sprintById: (serverId: string, boardId: number, sprintId: number) =>
      request<SprintView>(getToken, `/servers/${serverId}/sprint/${boardId}/sprints/${sprintId}`),
    sprintSummary: (serverId: string, boardId: number) =>
      request<{ externalRef: string; sessionId?: string }>(getToken, `/servers/${serverId}/sprint/${boardId}/summary`, { method: 'POST' }),
    credentials: (serverId: string) => request<{ credentials: ServerCredential[]; logins?: ServerLogin[]; running: boolean }>(getToken, `/servers/${serverId}/credentials`),
    startLogin: (serverId: string, kind: ServerCredential['kind'], name: string) =>
      request<ServerLogin>(getToken, `/servers/${serverId}/credentials/logins`, { method: 'POST', body: JSON.stringify({ kind, name }) }),
    people: {
      list: (serverId: string) => request<{ people: Person[]; backfill?: PeopleBackfill; github?: boolean }>(getToken, `/servers/${serverId}/people`),
      team: (serverId: string) => request<TeamView>(getToken, `/servers/${serverId}/people/team`),
      backfill: (serverId: string) => request<{ status: string }>(getToken, `/servers/${serverId}/people/backfill`, { method: 'POST', body: '{}' }),
      erase: (serverId: string, id: string) => request<void>(getToken, `/servers/${serverId}/people/${id}`, { method: 'DELETE' }),
      accept: (serverId: string, id: string, proposalId: string, text: string) =>
        request<Person>(getToken, `/servers/${serverId}/people/${id}/proposals/${proposalId}/accept`, { method: 'POST', body: JSON.stringify({ text }) }),
      drop: (serverId: string, id: string, proposalId: string) => request<Person>(getToken, `/servers/${serverId}/people/${id}/proposals/${proposalId}`, { method: 'DELETE' }),
      removeObservation: (serverId: string, id: string, observationId: string) =>
        request<Person>(getToken, `/servers/${serverId}/people/${id}/observations/${observationId}`, { method: 'DELETE' }),
      update: (serverId: string, id: string, fields: Partial<Pick<Person, 'name' | 'teams' | 'role' | 'growthTarget' | 'leftOn'>> & { priority?: boolean }) =>
        request<Person>(getToken, `/servers/${serverId}/people/${id}`, { method: 'PATCH', body: JSON.stringify(fields) }),
      link: (serverId: string, id: string, ref: string) =>
        request<void>(getToken, `/servers/${serverId}/people/${id}/accounts`, { method: 'POST', body: JSON.stringify({ ref }) }),
      acceptLink: (serverId: string, id: string, ref: string) =>
        request<void>(getToken, `/servers/${serverId}/people/${id}/links/${encodeURIComponent(ref)}/accept`, { method: 'POST' }),
      dropLink: (serverId: string, id: string, ref: string) =>
        request<void>(getToken, `/servers/${serverId}/people/${id}/links/${encodeURIComponent(ref)}`, { method: 'DELETE' }),
      separate: (serverId: string, id: string, ref: string) =>
        request<Person>(getToken, `/servers/${serverId}/people/${id}/accounts/${encodeURIComponent(ref)}`, { method: 'DELETE' }),
      history: (serverId: string, id: string) => request<{ history: PersonHistoryEntry[] }>(getToken, `/servers/${serverId}/people/${id}/history`),
      identity: (serverId: string, id: string) => request<PersonIdentity>(getToken, `/servers/${serverId}/people/${id}/identity`),
      linkIdentity: (serverId: string, id: string, other: { serverId: string; personId: string }) =>
        request<void>(getToken, `/servers/${serverId}/people/${id}/identity`, { method: 'POST', body: JSON.stringify(other) }),
      unlinkIdentity: (serverId: string, id: string) => request<void>(getToken, `/servers/${serverId}/people/${id}/identity`, { method: 'DELETE' }),
    },
    push: {
      publicKey: () => request<{ publicKey: string }>(getToken, '/push/public-key'),
      subscribe: (body: { subscription: unknown; scope: { scope: 'all' | 'servers'; servers: string[] }; label?: string }) =>
        request<{ id: string }>(getToken, '/push/subscriptions', { method: 'PUT', body: JSON.stringify(body) }),
      unsubscribe: (endpoint: string) =>
        request<void>(getToken, '/push/subscriptions', { method: 'DELETE', body: JSON.stringify({ endpoint }) }),
    },
    inbox: {
      list: (serverId: string) => request<InboxItem[]>(getToken, `/servers/${serverId}/inbox`),
      hidden: (serverId: string) => request<HiddenInboxItem[]>(getToken, `/servers/${serverId}/inbox?hidden=1`),
      snooze: (serverId: string, itemId: string, until: string) =>
        request<InboxItem[]>(getToken, `/servers/${serverId}/inbox/${encodeURIComponent(itemId)}/snooze`, { method: 'POST', body: JSON.stringify({ until }) }),
      dismiss: (serverId: string, itemId: string) =>
        request<InboxItem[]>(getToken, `/servers/${serverId}/inbox/${encodeURIComponent(itemId)}/dismiss`, { method: 'POST', body: '{}' }),
      setAside: (serverId: string, itemId: string, body: { reason: string; until?: string }) =>
        request<InboxItem[]>(getToken, `/servers/${serverId}/inbox/${encodeURIComponent(itemId)}/set-aside`, { method: 'POST', body: JSON.stringify(body) }),
      bringBack: (serverId: string, itemId: string) =>
        request<InboxItem[]>(getToken, `/servers/${serverId}/inbox/${encodeURIComponent(itemId)}/state`, { method: 'DELETE' }),
      /** Puts an item in another subject of the list, or in one of its own (ADR-039 step 4c) */
      moveToSubject: (serverId: string, itemId: string, to: string) =>
        request<InboxItem[]>(getToken, `/servers/${serverId}/inbox/${encodeURIComponent(itemId)}/subject`, { method: 'POST', body: JSON.stringify({ to }) }),
    },
    google: {
      status: (serverId: string) => request<GoogleStatus>(getToken, `/servers/${serverId}/google`),
      connect: (serverId: string) => request<{ url: string }>(getToken, `/servers/${serverId}/google/connect`, { method: 'POST', body: '{}' }),
      disconnect: (serverId: string) => request<void>(getToken, `/servers/${serverId}/google`, { method: 'DELETE' }),
      callback: (code: string, state: string) =>
        request<{ serverId: string; email: string }>(getToken, '/google/callback', { method: 'POST', body: JSON.stringify({ code, state }) }),
    },
    reports: {
      /** A report over a period (operator, 30/10/2026): the session that writes it */
      start: (serverId: string, body: { period: 'today' | 'days' | 'week' | 'sprint' | 'since-last'; days?: number }) =>
        request<{ sessionId: string; from: string; to: string; label: string }>(getToken, `/servers/${serverId}/reports`, { method: 'POST', body: JSON.stringify(body) }),
    },
    index: {
      status: (serverId: string) => request<IndexStatus>(getToken, `/servers/${serverId}/index`),
      fill: (serverId: string) => request<{ status: string; message: string }>(getToken, `/servers/${serverId}/index/backfill`, { method: 'POST', body: '{}' }),
    },
    slack: {
      status: (serverId: string) => request<SlackStatus>(getToken, `/servers/${serverId}/slack`),
      connect: (serverId: string) => request<{ url: string }>(getToken, `/servers/${serverId}/slack/connect`, { method: 'POST', body: '{}' }),
      disconnect: (serverId: string) => request<void>(getToken, `/servers/${serverId}/slack`, { method: 'DELETE' }),
      callback: (code: string, state: string) =>
        request<{ serverId: string; teamName: string }>(getToken, '/slack/callback', { method: 'POST', body: JSON.stringify({ code, state }) }),
      thread: (serverId: string, group: string) => request<SlackThread>(getToken, `/servers/${serverId}/slack/threads/${encodeURIComponent(group)}`),
      draft: (serverId: string, group: string) =>
        request<{ draft: string }>(getToken, `/servers/${serverId}/slack/threads/${encodeURIComponent(group)}/draft`, { method: 'POST', body: '{}' }),
      reply: (serverId: string, group: string, text: string) =>
        request<{ ts: string } | { unsure: true }>(getToken, `/servers/${serverId}/slack/threads/${encodeURIComponent(group)}/reply`, { method: 'POST', body: JSON.stringify({ text }) }),
    },
    notifications: {
      stats: (serverId: string) => request<NotificationStats>(getToken, `/servers/${serverId}/notifications`),
      saveSettings: (serverId: string, settings: NotifySettings) =>
        request<NotifySettings>(getToken, `/servers/${serverId}/notifications/settings`, { method: 'PUT', body: JSON.stringify(settings) }),
      restore: (serverId: string, kind: string) =>
        request<NotificationStats>(getToken, `/servers/${serverId}/notifications/restore`, { method: 'POST', body: JSON.stringify({ kind }) }),
      live: (serverId: string, until: string | null) =>
        request<NotificationStats & { flushed: number }>(getToken, `/servers/${serverId}/notifications/live`, { method: 'PUT', body: JSON.stringify({ until }) }),
      opened: (serverId: string, tag: string) =>
        request<{ recorded: boolean }>(getToken, `/servers/${serverId}/notifications/opened`, { method: 'POST', body: JSON.stringify({ tag }) }),
    },
    deployments: {
      list: (serverId: string) => request<DeploymentSummary[]>(getToken, `/servers/${serverId}/deployments`),
      get: (serverId: string, id: string) =>
        request<DeploymentDetail>(getToken, `/servers/${serverId}/deployments/${encodeURIComponent(id)}`),
      decide: (serverId: string, id: string, body: { state: 'approved' | 'rejected'; comment?: string; confirmDestroy?: number }) =>
        request<DeploymentSummary>(getToken, `/servers/${serverId}/deployments/${encodeURIComponent(id)}/decision`, { method: 'POST', body: JSON.stringify(body) }),
      startAnalysis: (serverId: string, id: string) =>
        request<{ status: string; externalRef: string; messageId: string }>(getToken, `/servers/${serverId}/deployments/${encodeURIComponent(id)}/analysis`, { method: 'POST' }),
    },
    held: {
      list: (serverId: string) => request<HeldEvent[]>(getToken, `/servers/${serverId}/held`),
      release: (serverId: string, heldId: string) =>
        request<{ status: string }>(getToken, `/servers/${serverId}/held/${encodeURIComponent(heldId)}/release`, { method: 'POST' }),
      dismiss: (serverId: string, heldId: string) =>
        request<void>(getToken, `/servers/${serverId}/held/${encodeURIComponent(heldId)}`, { method: 'DELETE' }),
    },
    sessions: {
      commands: (serverId: string) => request<{ commands: string[] }>(getToken, `/servers/${serverId}/sessions/commands`),
      readState: (serverId: string) => request<SessionReadState>(getToken, `/servers/${serverId}/sessions/read-state`),
      markRead: (serverId: string, sessionIds: string[]) =>
        request<{ readAt: string }>(getToken, `/servers/${serverId}/sessions/read`, { method: 'POST', body: JSON.stringify({ sessionIds }) }),
      list: (serverId: string, status?: string) => {
        const query = status ? `?status=${encodeURIComponent(status)}` : '';
        return request<Session[]>(getToken, `/servers/${serverId}/sessions${query}`);
      },
      get: (serverId: string, sessionId: string) =>
        request<Session>(getToken, `/servers/${serverId}/sessions/${sessionId}`),
      serverWaits: (serverId: string) => request<SessionWait[]>(getToken, `/servers/${serverId}/sessions/waits`),
      waits: (serverId: string, sessionId: string) =>
        request<SessionWait[]>(getToken, `/servers/${serverId}/sessions/${sessionId}/waits`),
      create: (serverId: string, body: { model?: string; name?: string }) =>
        request<Session>(getToken, `/servers/${serverId}/sessions`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      delete: (serverId: string, sessionId: string) =>
        request<void>(getToken, `/servers/${serverId}/sessions/${sessionId}`, {
          method: 'DELETE',
        }),
      restore: (serverId: string, sessionId: string) =>
        request<Session>(getToken, `/servers/${serverId}/sessions/${sessionId}/restore`, {
          method: 'POST',
        }),
      rename: (serverId: string, sessionId: string, name: string) =>
        request<Session>(getToken, `/servers/${serverId}/sessions/${sessionId}`, {
          method: 'PATCH',
          body: JSON.stringify({ name }),
        }),
      prompt: (serverId: string, sessionId: string, message: string) =>
        request<void>(getToken, `/servers/${serverId}/sessions/${sessionId}/prompt`, {
          method: 'POST',
          body: JSON.stringify({ message }),
        }),
      respond: (serverId: string, sessionId: string, promptId: string, response: string) =>
        request<void>(getToken, `/servers/${serverId}/sessions/${sessionId}/respond`, {
          method: 'POST',
          body: JSON.stringify({ promptId, response }),
        }),
      /** The sessions a facet points to, through their note or the object they work on */
      byFacet: (serverId: string, dimension: string, value: string) =>
        request<{ sessionIds: string[] }>(getToken, `/servers/${serverId}/sessions/by-facet?dimension=${encodeURIComponent(dimension)}&value=${encodeURIComponent(value)}`),
      /** What the index already says the session is about: its note's facets and its object's */
      facets: (serverId: string, sessionId: string) =>
        request<{ facets: Array<{ dimension: string; value: string }>; from: string[] }>(getToken, `/servers/${serverId}/sessions/${sessionId}/facets`),
      /** A secret for the session (ADR-043 section 2): only its name comes back */
      giveSecret: (serverId: string, sessionId: string, name: string, value: string) =>
        request<{ name: string }>(getToken, `/servers/${serverId}/sessions/${sessionId}/secrets`, {
          method: 'POST',
          body: JSON.stringify({ name, value }),
        }),
      interrupt: (serverId: string, sessionId: string) =>
        request<void>(getToken, `/servers/${serverId}/sessions/${sessionId}/interrupt`, {
          method: 'POST',
        }),
      switchModel: (serverId: string, sessionId: string, model: string) =>
        request<{ previousModel: string; newModel: string }>(getToken, `/servers/${serverId}/sessions/${sessionId}/switch-model`, {
          method: 'POST',
          body: JSON.stringify({ model }),
        }),
      history: (serverId: string, sessionId: string, sinceId?: number) => {
        const query = sinceId && sinceId > 0 ? `?since_id=${sinceId}` : '';
        return request<SSEEvent[]>(getToken, `/servers/${serverId}/sessions/${sessionId}/history${query}`);
      },
      replayHistory: (serverId: string, sessionId: string) =>
        request<SSEEvent[]>(getToken, `/servers/${serverId}/sessions/${sessionId}/history?replay=true`),
    },
    costs: {
      byServer: (serverId: string, since: string, until: string) =>
        request<CostsResponse>(getToken, `/servers/${serverId}/dashboard/costs?since=${since}&until=${until}`),
      aggregated: (since: string, until: string) =>
        request<AggregatedCostsResponse>(getToken, `/dashboard/costs?since=${since}&until=${until}`),
    },
    consumption: {
      byServer: (serverId: string, since: string, until: string) =>
        request<ConsumptionResponse>(getToken, `/servers/${serverId}/dashboard/consumption?since=${since}&until=${until}`),
    },
    calendar: {
      proposals: (serverId: string) => request<{ proposals: CalendarProposal[] }>(getToken, `/servers/${serverId}/calendar/proposals`),
      decide: (serverId: string, proposalId: string, decision: 'approve' | 'reject') =>
        request<{ proposal: CalendarProposal }>(getToken, `/servers/${serverId}/calendar/proposals/${encodeURIComponent(proposalId)}/${decision}`, { method: 'POST' }),
    },
    documents: {
      proposals: (serverId: string) => request<{ proposals: DocumentProposal[] }>(getToken, `/servers/${serverId}/documents/proposals`),
      decide: (serverId: string, proposalId: string, decision: 'approve' | 'reject') =>
        request<{ proposal: DocumentProposal }>(getToken, `/servers/${serverId}/documents/proposals/${encodeURIComponent(proposalId)}/${decision}`, { method: 'POST' }),
    },
    outbox: {
      improvements: (serverId: string) => request<{ improvements: ImprovementProposal[] }>(getToken, `/servers/${serverId}/outbox/improvements`),
      actions: (serverId: string) => request<{ actions: ActionProposal[] }>(getToken, `/servers/${serverId}/outbox/actions`),
      slack: (serverId: string, sessionId: string) => request<{ proposals: SlackProposal[] }>(getToken, `/servers/${serverId}/outbox/slack?sessionId=${encodeURIComponent(sessionId)}`),
      sendSlack: (serverId: string, id: string, text: string) =>
        request<{ proposal: SlackProposal }>(getToken, `/servers/${serverId}/outbox/slack/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify({ text }) }),
      rejectSlack: (serverId: string, id: string) =>
        request<{ proposal: SlackProposal }>(getToken, `/servers/${serverId}/outbox/slack/${encodeURIComponent(id)}/reject`, { method: 'POST', body: '{}' }),
      decideAction: (serverId: string, id: string, decision: 'approve' | 'reject') =>
        request<{ action: ActionProposal }>(getToken, `/servers/${serverId}/outbox/actions/${encodeURIComponent(id)}/${decision}`, { method: 'POST', body: '{}' }),
      open: (serverId: string, id: string, edits: { title?: string; body?: string; duplicateOf?: number }) =>
        request<{ improvement: ImprovementProposal }>(getToken, `/servers/${serverId}/outbox/improvements/${encodeURIComponent(id)}/open`, { method: 'POST', body: JSON.stringify(edits) }),
      issues: (serverId: string) => request<{ issues: AidevIssue[] }>(getToken, `/servers/${serverId}/outbox/improvements/issues`),
      dismiss: (serverId: string, id: string) =>
        request<{ improvement: ImprovementProposal }>(getToken, `/servers/${serverId}/outbox/improvements/${encodeURIComponent(id)}/dismiss`, { method: 'POST' }),
      messages: (serverId: string) => request<{ messages: OutboxMessage[] }>(getToken, `/servers/${serverId}/outbox/messages`),
      send: (serverId: string, id: string, sessionId: string | null, opts: { allowConversation?: boolean; start?: boolean } = {}) =>
        request<{ message: OutboxMessage }>(getToken, `/servers/${serverId}/outbox/messages/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify({ sessionId, ...opts }) }),
      conversations: (serverId: string) => request<{ conversations: MessageConversation[] }>(getToken, `/servers/${serverId}/outbox/conversations`),
      stopConversation: (serverId: string, c: Pick<MessageConversation, 'session' | 'peer' | 'peerSession'>) =>
        request<{ stopped: boolean }>(getToken, `/servers/${serverId}/outbox/conversations/stop`, { method: 'POST', body: JSON.stringify({ session: c.session, peer: c.peer, peerSession: c.peerSession }) }),
      reject: (serverId: string, id: string) =>
        request<{ message: OutboxMessage }>(getToken, `/servers/${serverId}/outbox/messages/${encodeURIComponent(id)}/reject`, { method: 'POST' }),
      peers: (serverId: string) => request<{ peers: string[] }>(getToken, `/servers/${serverId}/outbox/peers`),
      setPeers: (serverId: string, peers: string[]) =>
        request<{ peers: string[] }>(getToken, `/servers/${serverId}/outbox/peers`, { method: 'PUT', body: JSON.stringify({ peers }) }),
    },
    costThreshold: {
      get: (serverId: string) => request<CostStatus>(getToken, `/servers/${serverId}/cost-threshold`),
      set: (serverId: string, monthlyUsd: number | null) =>
        request<CostStatus>(getToken, `/servers/${serverId}/cost-threshold`, { method: 'PUT', body: JSON.stringify({ monthlyUsd }) }),
    },
    workspace: {
      tree: (serverId: string, path?: string, depth?: number) => {
        const params = new URLSearchParams();
        if (path) params.set('path', path);
        if (depth !== undefined) params.set('depth', String(depth));
        const query = params.toString();
        return request<WorkspaceTreeResponse>(getToken, `/servers/${serverId}/workspace/tree${query ? `?${query}` : ''}`);
      },
      file: (serverId: string, path: string) =>
        request<WorkspaceFileResponse>(getToken, `/servers/${serverId}/workspace/file?path=${encodeURIComponent(path)}`),
      gitStatus: (serverId: string, path?: string) => {
        const query = path ? `?path=${encodeURIComponent(path)}` : '';
        return request<WorkspaceGitStatusResponse>(getToken, `/servers/${serverId}/workspace/git/status${query}`);
      },
      gitBranches: (serverId: string, path?: string) => {
        const query = path ? `?path=${encodeURIComponent(path)}` : '';
        return request<WorkspaceGitBranchesResponse>(getToken, `/servers/${serverId}/workspace/git/branches${query}`);
      },
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
